import React from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Loader2, MapPin, ExternalLink } from 'lucide-react'
import type { Gallery } from '@/lib/supabase'

interface GalleryPreviewProps {
  galleryId: number
}

/**
 * Gallery Preview Component
 *
 * Compact display showing linked gallery info with link to full gallery.
 * Images are managed separately via Custom Gallery Images section below.
 */
export default function GalleryPreview({ galleryId }: GalleryPreviewProps) {
  const { data: gallery, isLoading, isError } = useQuery({
    queryKey: ['gallery', galleryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('galleries')
        .select('*')
        .eq('id', galleryId)
        .single()

      if (error) throw error
      return data as Gallery
    },
    enabled: !!galleryId && galleryId > 0
  })

  if (!galleryId || galleryId <= 0) {
    return null
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading gallery...</span>
      </div>
    )
  }

  if (isError || !gallery) {
    return (
      <div className="text-sm text-destructive py-2">
        Failed to load gallery
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between gap-4 p-3 bg-muted/50 rounded-lg border">
      <div className="flex items-center gap-3 min-w-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{gallery.title}</span>
            <Badge
              variant={gallery.review_status === 'approved' ? 'default' : 'secondary'}
              className="shrink-0"
            >
              {gallery.review_status}
            </Badge>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
            <MapPin className="h-3 w-3" />
            <span>{gallery.franchise_region?.replace('_', ' ') || 'Unknown'}</span>
            {gallery.job_number && <span>• Job #{gallery.job_number}</span>}
          </div>
        </div>
      </div>
      <Link to={`/media/galleries/${galleryId}`}>
        <Button variant="outline" size="sm" className="shrink-0">
          <ExternalLink className="mr-2 h-4 w-4" />
          View Gallery
        </Button>
      </Link>
    </div>
  )
}
